/**
 * voice.js - what Pip says in Door Knock.
 *
 * The praise, encouragement, near-miss and assist pools are the PLATFORM's
 * (../../shared/voice.js). This game adds the handful of lines only a game
 * about doors can say, and the prompts, which are the part no shared library
 * can supply because they belong to the phase and the mechanic together.
 */

import * as SHARED from '../../shared/voice.js';
import { mergePools } from '../../shared/voice.js';

export const PRAISE = mergePools(SHARED.PRAISE, [
  { text: 'The door swings open.', tone: 'bright' },
  { text: 'Out they come.', tone: 'bright' },
  { text: 'That knock was heard.', tone: 'bright' },
  { text: 'Somebody was listening.', tone: 'calm' },
  { text: 'In they go.', tone: 'calm' },
]);

export const ENCOURAGEMENT = mergePools(SHARED.ENCOURAGEMENT, [
  { text: 'Somebody is behind the door. Keep going.', tone: 'calm' },
  { text: 'Pip heard you knock.', tone: 'calm' },
]);

export const NEARLY = mergePools(SHARED.NEARLY, [
  { text: 'That door stayed shut. Listen once more.', tone: 'calm' },
]);

export const ASSIST = mergePools(SHARED.ASSIST, [
  { text: 'Pip will knock this time.', tone: 'calm' },
]);

/**
 * The prompt, by phase and by support rung.
 *
 * Rung ids are SUPPORT_LADDER values from the engine, and only rungs inside the
 * phase's own bounds may appear. A rung that is supposed to withhold the answer
 * must contain no {word} and no {phrase} - the conformance test checks it, and
 * it is the easiest rule in the contract to break by accident.
 *
 * Placeholders: {word} {phrase} {letter} {clue} {carrier} {carriers} {place}
 */
export const PROMPTS = {
  2: {
    visual: [
      'Who is behind this door?',
      'Who is that?',
      'Somebody is knocking. Who?',
      'Have a look. Who is there?',
    ],
    partial_verbal: [
      'It starts with {letter}.',
      '{letter}. Who is it?',
      'Listen. {letter}. Who is behind there?',
    ],
    full_model: [
      'It is a {word}. Say {word}.',
      '{word}. Now you say {word}.',
    ],
  },
  3: {
    gestural: [
      'Which one shall we call?',
      'Tell Pip what to do.',
      'Whose turn is it?',
    ],
    visual: [
      'Two words, and the door opens.',
      'Tell Pip in two words.',
      'What would you like?',
    ],
    partial_verbal: [
      '{carrier}, and then who?',
      'Start with {carrier}.',
    ],
    full_model: [
      'Say {phrase}.',
      'Like this. {phrase}. Now you.',
    ],
  },
  4: {
    positional: [
      'Where does it go?',
      'Tell Pip where.',
      'And now?',
    ],
    gestural: [
      'What is happening?',
      'Tell Pip what you are doing.',
      'Say the whole thing.',
    ],
    visual: [
      'Use the word for what you are doing, and where.',
      'What is happening, and where does it go?',
    ],
    partial_verbal: [
      'It starts with putting.',
      'Begin with putting.',
    ],
    full_model: [
      'Say {phrase}.',
      'Like this. {phrase}. Your turn.',
    ],
  },
};

export const HINTS = {
  2: ['{clue}', 'A clue for you. {clue}'],
  3: ['You can say {carriers}, and then who.', 'Two words. One from each side.'],
  4: ['Say what is happening, then where it goes.', 'It is going {place}.'],
};

export const LEVEL_INTRO = {
  2: [
    'Someone is behind each door. Say who, and the door opens.',
    'Knock knock. Say who is there.',
  ],
  3: [
    'Now ask for one. Two words.',
    'Tell Pip who you want, in two words.',
  ],
  4: [
    'Now say what is happening, and where it goes.',
    'Tell Pip what you are doing with them.',
  ],
};

export const ROUND_LINKS = [
  'Another door.',
  'Someone else is knocking.',
  'Look at this one.',
  'And now this door.',
];

export const LEVEL_COMPLETE = [
  'Every door opened.',
  'That is the whole street.',
  'All of them, out.',
];

export const SESSION_END = [
  'Thank you for knocking with Pip.',
  'Pip had a lovely time. Come back soon.',
];

/** Fill {placeholders}. An unknown one is left visible rather than blanked. */
export function renderLine(template, vars = {}) {
  return String(template).replace(/\{(\w+)\}/g, (whole, key) =>
    (vars[key] === undefined || vars[key] === null ? whole : String(vars[key])));
}

export default {
  PRAISE, ENCOURAGEMENT, NEARLY, ASSIST,
  PROMPTS, HINTS, LEVEL_INTRO, ROUND_LINKS, LEVEL_COMPLETE, SESSION_END, renderLine,
};
